import {
  PRO_PRICE_CURRENCY,
  PRO_PRICE_PER_MONTH,
  PRO_REGION_NOTE,
  PRO_TRIAL_SUMMARY,
  proPricePerMonth,
} from "~/utils/pricing";
import { useEffect } from "react";
import { Alert, Platform } from "react-native";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import { ActivityIndicator } from "react-native";

import { AuthScreen } from "~/components/composed/AuthScreen";
import {
  AppText,
  Button,
  ButtonText,
  Card,
  CenteredScreen,
  Stack,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { usePurchases } from "~/hooks/usePurchases";
import type { PurchasePlan } from "~/services/purchases";
import {
  getPostAuthPath,
  hasPaidAccess,
  ONBOARDING_PATH,
} from "~/utils/onboarding";
import {
  clearPendingCheckoutIntent,
  normalizeSignupIntent,
} from "~/utils/signup-intent";

function packageLabel(pkg: {
  billingPeriod: "monthly" | "annual" | "other";
  plan: PurchasePlan;
  priceText: string;
  title: string;
}) {
  if (pkg.billingPeriod === "monthly" || pkg.plan === "pro") {
    return `MintStint Pro — ${proPricePerMonth(pkg.priceText)}`;
  }
  return `${pkg.title} — ${pkg.priceText}`;
}

export default function SubscribeScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ plan?: string; billing?: string }>();
  const { session, user, loading, logout, logoutPending, refresh } = useAuth();
  const purchases = usePurchases(user?.id ?? null);
  const intent = normalizeSignupIntent(params);

  useEffect(() => {
    // Keep monthly-only intent; ignore annual packages in the primary CTA.
    void intent;
  }, [intent]);

  if (loading) {
    return (
      <CenteredScreen>
        <ActivityIndicator />
      </CenteredScreen>
    );
  }

  if (!session?.user) {
    return <Redirect href="/login" />;
  }

  if (hasPaidAccess(user)) {
    return <Redirect href={getPostAuthPath(user) as never} />;
  }

  const monthlyPackages = purchases.packages.filter(
    (pkg) => pkg.billingPeriod === "monthly" || pkg.plan === "pro",
  );
  const displayPackages =
    monthlyPackages.length > 0 ? monthlyPackages : purchases.packages;
  const useNativePaywall =
    purchases.supportsNativePaywall && Platform.OS !== "web";

  const afterPurchase = async () => {
    clearPendingCheckoutIntent();
    const nextUser = await refresh();
    router.replace(
      (nextUser && !hasPaidAccess(nextUser)
        ? "/subscribe"
        : ONBOARDING_PATH) as never,
    );
  };

  return (
    <AuthScreen
      title="Activate MintStint Pro"
      subtitle={`${PRO_TRIAL_SUMMARY}. Billed in ${PRO_PRICE_CURRENCY}. ${PRO_REGION_NOTE}`}
    >
      <Stack gap="md">
        <Card gap="sm" compact>
          <AppText size="xs">
            Confirm your email is done — next, add payment to unlock your
            workspace. You can manage or cancel anytime from Settings.
          </AppText>
        </Card>

        {purchases.error ? (
          <AppText tone="danger" size="xs">
            {purchases.error}
          </AppText>
        ) : null}

        {useNativePaywall ? (
          <Button
            disabled={purchases.purchasing || !purchases.configured}
            onPress={async () => {
              try {
                const result = await purchases.presentPaywall();
                if (result === "purchased" || result === "restored") {
                  await afterPurchase();
                  Alert.alert(
                    "Purchase complete",
                    "Your MintStint Pro subscription is updating.",
                  );
                }
              } catch (error) {
                Alert.alert(
                  "Purchase failed",
                  error instanceof Error ? error.message : "Please try again.",
                );
              }
            }}
          >
            <ButtonText>
              {purchases.purchasing
                ? "Opening..."
                : `Subscribe — ${PRO_PRICE_PER_MONTH}`}
            </ButtonText>
          </Button>
        ) : (
          displayPackages.map((pkg) => (
            <Button
              key={pkg.identifier}
              disabled={purchases.purchasing}
              onPress={async () => {
                try {
                  await purchases.purchase(pkg);
                  await afterPurchase();
                  Alert.alert(
                    "Purchase complete",
                    "Your MintStint Pro subscription is updating.",
                  );
                } catch (error) {
                  if (
                    error &&
                    typeof error === "object" &&
                    "userCancelled" in error &&
                    (error as { userCancelled?: boolean }).userCancelled
                  ) {
                    return;
                  }
                  Alert.alert(
                    "Purchase failed",
                    error instanceof Error
                      ? error.message
                      : "Please try again.",
                  );
                }
              }}
            >
              <ButtonText>
                {purchases.purchasing ? "Processing..." : packageLabel(pkg)}
              </ButtonText>
            </Button>
          ))
        )}

        <Button
          variant="outline"
          disabled={!purchases.configured || purchases.loading}
          onPress={async () => {
            try {
              await purchases.restore();
              await afterPurchase();
              Alert.alert("Purchases restored");
            } catch (error) {
              Alert.alert(
                "Restore failed",
                error instanceof Error ? error.message : "Please try again.",
              );
            }
          }}
        >
          <ButtonText>
            {purchases.loading ? "Loading plans..." : "Restore purchases"}
          </ButtonText>
        </Button>

        {!purchases.loading && purchases.packages.length === 0 ? (
          <AppText size="xs">
            Subscription plans are not configured for this environment yet.
          </AppText>
        ) : null}

        <Button
          variant="outline"
          onPress={() => void logout()}
          disabled={logoutPending}
        >
          <ButtonText>
            {logoutPending ? "Signing out..." : "Sign out"}
          </ButtonText>
        </Button>
      </Stack>
    </AuthScreen>
  );
}
